/**
 * Labstep
 *
 * @module grid/NewIndex/components/ColumnHeader/ContextMenu
 * @desc Entity DataGrid Context Menu
 */

import { EntityActionDelete } from 'labstep-web/components/Entity/Action/Delete';
import {
  useDataGridContext,
  useGridSmartTableProtocolValueServiceFromContext,
} from 'labstep-web/contexts/grid/hook';
import { useParamsStateContext } from 'labstep-web/contexts/params-state/hook';
import { Dropdown } from 'labstep-web/core/Dropdown';
import { DropdownPortal } from 'labstep-web/core/Dropdown/Portal';
import { Icon } from 'labstep-web/core/Icon';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import React from 'react';
import type { GridSmartTableColumnHeaderContextMenuProps } from './types';

export const GridNewIndexColumnHeaderContextMenu: React.FC<
  GridSmartTableColumnHeaderContextMenuProps
> = ({
  columnApi,
  column,
  firstItems,
  lastItems,
  trigger,
  experimentOrProtocol,
  metadata,
}) => {
  const ctx = useToolPanelContext();

  const { agGrid } = useDataGridContext();
  const gridSmartTableProtocolValueService =
    useGridSmartTableProtocolValueServiceFromContext();
  const { entityView } = useParamsStateContext();

  const dropdownItems: React.ReactNode[] = firstItems
    ? [firstItems]
    : [];

  const afterDividerItems = [];

  const colDef = column.getColDef() as SmartTableColDef;

  const parts: string[] = (colDef.colId || '').split(':');

  if (
    parts.length >= 4 &&
    parts[0] === 'metadata' &&
    (parts[1] === 'experiment' || parts[1] === 'protocol') &&
    metadata &&
    entityView &&
    gridSmartTableProtocolValueService
  ) {
    dropdownItems.push(
      <EntityActionDelete
        key="delete-metadata"
        dataCy="grid/SmartTableProtocolValue/components/ColumnHeader/ContextMenu:data-field-delete"
        entity={metadata}
        actionComponentProps={{ type: 'option' }}
        options={{
          onSuccess: () => {
            gridSmartTableProtocolValueService.deleteDataFieldColDefs(
              metadata,
              entityView,
            );
          },
        }}
      />,
    );
  }

  if (
    experimentOrProtocol &&
    !experimentOrProtocol.isLocked &&
    GridNewIndexService.isHideable(column)
  ) {
    afterDividerItems.push(
      <Dropdown.Item
        key="hide-column"
        onClick={(): void =>
          columnApi.setColumnVisible(column, false)
        }
      >
        <Icon name="eye slash" /> Hide from View
      </Dropdown.Item>,
    );
  }

  if (lastItems) {
    afterDividerItems.push(
      <React.Fragment key="last-items">{lastItems}</React.Fragment>,
    );
  }
  if (dropdownItems.length && afterDividerItems.length) {
    dropdownItems.push(<Dropdown.Divider key="main-divider" />);
  }
  dropdownItems.push(...afterDividerItems);
  if (colDef.columnOptions) {
    dropdownItems.push(<Dropdown.Divider key="options-divider" />);
    dropdownItems.push(
      <React.Fragment key="column-options">
        {colDef.columnOptions}
      </React.Fragment>,
    );
  }
  return dropdownItems.length ? (
    <DropdownPortal trigger={trigger}>{dropdownItems}</DropdownPortal>
  ) : (
    trigger
  );
};
